// frontend/src/pages/landing/LandingPage.tsx
/**
 * Pensado por: Alcélio Gomes
 * Codificado em coautoria: Claude Code
 * 🚀 NeuroAgentes
 * 
 * AUDITORIA: Página de landing da plataforma NeuroAgentes.
 * Inclui apresentação da plataforma, features e chamadas para ação.
 */

import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  useMediaQuery,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Divider,
  alpha,
  Chip,
  Fade,
  Zoom,
  Slide,
  Link,
  IconButton
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  WhatsApp as WhatsAppIcon,
  Psychology as PsychologyIcon,
  WebhookOutlined as WebhookIcon,
  Analytics as AnalyticsIcon,
  Shield as ShieldIcon,
  PersonAdd as PersonAddIcon,
  Login as LoginIcon,
  Check as CheckIcon,
  Speed as SpeedIcon,
  AutoAwesome as AutoAwesomeIcon,
  Bolt as BoltIcon,
  Devices as DevicesIcon,
  Security as SecurityIcon,
  Lightbulb as LightbulbIcon,
  Storefront as StorefrontIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Groups as GroupsIcon,
  Code as CodeIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
  ArrowForward as ArrowForwardIcon,
  PlayArrow as PlayArrowIcon,
  SupportAgent as SupportAgentIcon,
  AttachMoney as AttachMoneyIcon,
  Api as ApiIcon,
  IntegrationInstructions as IntegrationInstructionsIcon,
  Favorite as FavoriteIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { 
  VERDE_MENTA, 
  VERDE_MENTA_ESCURO, 
  VERDE_MENTA_CLARO,
  FUNDO_ESCURO,
  FUNDO_PAPEL,
  FUNDO_DRAWER,
  TEXTO_PRIMARIO,
  TEXTO_SECUNDARIO,
  withAlpha
} from '../../themes/themeConstants';
import LandingMenu from '../../components/layout/LandingMenu';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const features = [
    {
      title: 'Conecta e Funciona em 3 Minutos',
      description: 'Responde 3 perguntas sobre seu negócio, conecta seu WhatsApp e pronto! Agente já está trabalhando. Zero instalação, zero configuração técnica. É SaaS na nuvem.',
      icon: <BoltIcon fontSize="large" color="primary" />,
      image: '/images/avatars/preview-robot.png'
    },
    {
      title: 'Você Só Alimenta com Informações',
      description: 'Sua única tarefa: abastecer o agente com dados do seu negócio. Catálogos, preços, políticas. Sistema absorve tudo e já sai falando como especialista da empresa.',
      icon: <PsychologyIcon fontSize="large" color="primary" />,
      image: '/images/avatars/preview-robot.png'
    },
    {
      title: 'Na Nuvem, Sempre Disponível',
      description: 'SaaS na nuvem com 99.9% de disponibilidade. Não se preocupe com servidores, backups ou atualizacões. Você acessa de qualquer lugar, sistema sempre funcionando.',
      icon: <SecurityIcon fontSize="large" color="primary" />,
      image: '/images/integration-diagram.png'
    },
    {
      title: 'Assistente Para Seus Vendedores',
      description: 'Seus vendedores ganham um assistente IA nas plataformas digitais. Informações instantâneas, suporte em tempo real. Quando offline, o agente assume automaticamente.',
      icon: <AnalyticsIcon fontSize="large" color="primary" />,
      image: '/images/selective-mode-illustration.png'
    }
  ];

  const plans = [
    {
      title: 'Starter',
      price: 'Em breve',
      originalPrice: 'R$ 297/mês',
      features: [
        'Equipe digital funcionando em 3 minutos',
        'Até 3 especialistas para seu negócio',
        'Entende e fala como brasileiro',
        'Modelos prontos para seu setor',
        'Trabalha 24/7 sem parar',
        'Suporte sempre que precisar'
      ],
      highlighted: false
    },
    {
      title: 'Professional',
      price: 'Em breve',
      originalPrice: 'R$ 597/mês',
      features: [
        'Tudo do Starter + superpoderes',
        'Até 10 especialistas trabalhando',
        'Identifica clientes insatisfeitos',
        'Aprende sozinho com seus documentos',
        'Toda equipe pode usar',
        'Integra com sistemas que você já usa',
        'Atendimento prioritário'
      ],
      highlighted: true
    },
    {
      title: 'Enterprise',
      price: 'Em breve',
      originalPrice: 'R$ 1.497/mês',
      features: [
        'Equipe digital ilimitada',
        'Infraestrutura que nunca falha',
        'Máxima segurança para seus dados',
        'Garantia 99.9% de funcionamento',
        'Nós implementamos tudo para você',
        'Personalizado do seu jeito',
        'Consultor dedicado só para você'
      ],
      highlighted: false
    }
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header/NavBar */}
      <LandingMenu currentPage="home" />

      {/* Hero Section */}
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        sx={{
          backgroundColor: FUNDO_PAPEL,
          backgroundImage: `radial-gradient(circle at 25% 25%, ${withAlpha(VERDE_MENTA, 0.15)} 0%, transparent 20%), 
                           radial-gradient(circle at 75% 75%, ${withAlpha(VERDE_MENTA, 0.1)} 0%, transparent 20%)`,
          minHeight: '95vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Animated background elements */}
        <Box 
          component={motion.div}
          animate={{ 
            y: [0, -10, 0],
            opacity: [0.5, 0.8, 0.5]
          }}
          transition={{ 
            repeat: Infinity,
            duration: 5,
            ease: "easeInOut"
          }}
          sx={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${withAlpha(VERDE_MENTA, 0.2)} 0%, transparent 70%)`,
            filter: 'blur(40px)',
            zIndex: 0
          }}
        />
        <Box 
          component={motion.div}
          animate={{ 
            y: [0, 15, 0],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ 
            repeat: Infinity,
            duration: 7,
            ease: "easeInOut",
            delay: 0.5
          }}
          sx={{
            position: 'absolute',
            bottom: '15%',
            right: '10%',
            width: '250px',
            height: '250px',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${withAlpha(VERDE_MENTA_ESCURO, 0.15)} 0%, transparent 70%)`,
            filter: 'blur(40px)',
            zIndex: 0
          }}
        />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, px: { xs: 3, md: 6 } }}>
          <Grid container spacing={4} alignItems="flex-start" sx={{ height: '100%' }}>
            <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Box>
                <Box sx={{ mb: 5 }}>
                  <Chip 
                    component={motion.div}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    label="🚀 O Poder da IA Para Todos os Negócios" 
                    color="primary" 
                    icon={<AutoAwesomeIcon />} 
                    sx={{ 
                      fontWeight: 'bold',
                      background: `linear-gradient(135deg, ${withAlpha(VERDE_MENTA, 0.2)} 0%, ${withAlpha('#00ACC1', 0.2)} 100%)`,
                      color: VERDE_MENTA,
                      border: `1px solid ${VERDE_MENTA}`,
                      boxShadow: `0 0 20px ${withAlpha(VERDE_MENTA, 0.3)}`,
                      '& .MuiChip-icon': { color: VERDE_MENTA }
                    }} 
                  />
                </Box>
                
                <Typography 
                  component={motion.h1}
                  variant="h2" 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  gutterBottom 
                  sx={{ 
                    fontWeight: 'bold', 
                    mb: 3, 
                    color: TEXTO_PRIMARIO,
                    textShadow: `0 0 15px ${withAlpha(VERDE_MENTA, 0.5)}`,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    lineHeight: 1.1
                  }}
                >
                  Superpotencialize Seu{' '}
                  <Box component="span" sx={{ 
                    background: `linear-gradient(90deg, ${VERDE_MENTA} 0%, #00ACC1 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    textShadow: `0 0 30px ${withAlpha(VERDE_MENTA, 0.5)}`
                  }}>
                    Relacionamento Com Clientes
                  </Box>
                </Typography>
                
                <Typography 
                  component={motion.p}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  variant="h5" 
                  paragraph 
                  sx={{ 
                    mb: 5, 
                    maxWidth: 700, 
                    color: TEXTO_SECUNDARIO,
                    fontSize: { xs: '1.1rem', md: '1.3rem' },
                    lineHeight: 1.6
                  }}
                >
                  <Box component="span" sx={{ color: TEXTO_PRIMARIO, fontWeight: 'bold' }}>
                    Vende, atende, resolve problemas e cuida dos clientes
                  </Box>
                  {' '}sem parar, sem reclamar, sem pedir aumento. A CONEXOR trabalha 24/7 
                  enquanto você foca no crescimento do negócio. 
                  <Box component="span" sx={{ 
                    background: `linear-gradient(90deg, #00ACC1 0%, ${VERDE_MENTA} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    O poder da IA para turbinar seus resultados.
                  </Box>
                </Typography>

                {/* Estatísticas impactantes */}
                <Box 
                  component={motion.div}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  sx={{ 
                    mb: 4,
                    display: 'flex',
                    gap: 3,
                    flexWrap: 'wrap',
                    justifyContent: { xs: 'center', md: 'flex-start' }
                  }}
                >
                  {[
                    { stat: '3 min', desc: 'Para integrar e estar funcionando', gradient: `linear-gradient(135deg, ${VERDE_MENTA} 0%, #00ACC1 100%)` },
                    { stat: '100%', desc: 'Na nuvem, zero instalação', gradient: `linear-gradient(135deg, #00ACC1 0%, ${VERDE_MENTA} 100%)` },
                    { stat: '24/7', desc: 'Trabalhando automaticamente', gradient: `linear-gradient(135deg, ${VERDE_MENTA} 0%, #00ACC1 100%)` }
                  ].map((item, index) => (
                    <Box 
                      key={index}
                      sx={{
                        p: 3,
                        background: `linear-gradient(135deg, ${withAlpha('#000', 0.6)} 0%, ${withAlpha(FUNDO_PAPEL, 0.8)} 100%)`,
                        borderRadius: '16px',
                        border: `1px solid ${VERDE_MENTA}`,
                        textAlign: 'center',
                        minWidth: '140px',
                        boxShadow: `0 8px 32px ${withAlpha(VERDE_MENTA, 0.2)}`,
                        backdropFilter: 'blur(10px)'
                      }}
                    >
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          fontWeight: 'bold', 
                          background: item.gradient,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          mb: 1
                        }}
                      >
                        {item.stat}
                      </Typography>
                      <Typography variant="caption" sx={{ color: TEXTO_SECUNDARIO }}>
                        {item.desc}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                
                <Box 
                  component={motion.div}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9 }}
                  sx={{ 
                    display: 'flex', 
                    gap: 3, 
                    flexWrap: 'wrap',
                    justifyContent: { xs: 'center', md: 'flex-start' }
                  }}
                >
                  <Button 
                    component={motion.button}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    variant="contained" 
                    size="large" 
                    onClick={() => navigate('/register')}
                    startIcon={<PersonAddIcon />}
                    sx={{
                      background: `linear-gradient(135deg, ${VERDE_MENTA} 0%, #00ACC1 50%, ${VERDE_MENTA} 100%)`,
                      color: '#000',
                      fontWeight: 'bold',
                      px: 4,
                      py: 2,
                      fontSize: '1.1rem',
                      borderRadius: '30px',
                      boxShadow: `0 8px 30px ${withAlpha(VERDE_MENTA, 0.4)}, 0 0 0 1px ${withAlpha('#00ACC1', 0.3)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${VERDE_MENTA_CLARO} 0%, #4CC9F0 50%, ${VERDE_MENTA_CLARO} 100%)`,
                        boxShadow: `0 12px 40px ${withAlpha(VERDE_MENTA, 0.6)}, 0 0 0 2px ${withAlpha('#00ACC1', 0.5)}`,
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    🚀 Superpotencializar Agora
                  </Button>
                  
                  <Button 
                    component={motion.button}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    variant="outlined" 
                    size="large" 
                    onClick={() => window.open('/demo', '_blank')}
                    startIcon={<PlayArrowIcon />}
                    sx={{
                      color: TEXTO_PRIMARIO,
                      borderColor: VERDE_MENTA,
                      borderWidth: 2,
                      px: 4,
                      py: 2,
                      fontSize: '1.1rem',
                      borderRadius: '30px',
                      '&:hover': {
                        borderColor: VERDE_MENTA_CLARO,
                        borderWidth: 2,
                        backgroundColor: withAlpha(VERDE_MENTA, 0.1)
                      }
                    }}
                  >
                    📹 Ver CONEXOR em Ação (2min)
                  </Button>
                </Box>
                
                <Box 
                  component={motion.div}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.2 }}
                  sx={{ 
                    mt: 4, 
                    textAlign: { xs: 'center', md: 'left' }
                  }}
                >
                  <Typography variant="body1" sx={{ color: VERDE_MENTA, fontWeight: 'bold', mb: 1 }}>
                    ✅ Conecta em 3 minutos • ✅ Teste grátis 14 dias • ✅ Sem contrato ou fidelidade
                  </Typography>
                  <Typography variant="body2" sx={{ color: TEXTO_SECUNDARIO }}>
                    📈 Mais de 500 empresas já pararam de perder vendas
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box 
                component={motion.div}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ 
                  delay: 0.8,
                  type: "spring",
                  stiffness: 100
                }}
                sx={{ 
                  position: 'relative',
                  width: '100%',
                  mt: 0,
                  pt: 2,
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <Box sx={{ maxWidth: '1000px', width: '100%' }}>
                    <Grid container spacing={2} alignItems="flex-start" justifyContent="center">
                  {/* Card 1: SEM O CONEXOR */}
                  <Grid item xs={12} md={12} lg={12} xl={6}>
                    <Box 
                      component={motion.div}
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ 
                        delay: 0.5,
                        duration: 0.6
                      }}
                      sx={{
                        backgroundColor: FUNDO_PAPEL,
                        borderRadius: 3,
                        px: 6,
                        py: 3.5,
                        minWidth: '400px',
                        border: `2px solid ${withAlpha('#FF6B6B', 0.4)}`,
                        boxShadow: `0 8px 24px ${withAlpha('#FF6B6B', 0.2)}`,
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <Typography variant="h6" sx={{ color: '#FF6B6B', fontWeight: 'bold', mb: 2, textAlign: 'center', fontSize: '1.1rem' }}>
                        ❌ SEM O CONEXOR
                      </Typography>
                      
                      <Box sx={{ flex: 1 }}>
                        {[
                          { text: "• Cliente pergunta às 14h", delay: 1 },
                          { text: "• Você vê às 14h20min", delay: 2 },
                          { text: "• Concorrente já vendeu às 14h02min", delay: 3 },
                          { text: "• Você perdeu mais uma venda", delay: 4, isLast: true }
                        ].map((item, index) => (
                          <Box 
                            key={index}
                            component={motion.div}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: [0, 1, 1, 0], x: [-20, 0, 0, -20] }}
                            transition={{ 
                              repeat: Infinity,
                              duration: 8,
                              delay: item.delay,
                              times: [0, 0.1, 0.9, 1]
                            }}
                            sx={{ mb: 2.5, minHeight: '28px' }}
                          >
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: item.isLast ? '#FF6B6B' : TEXTO_PRIMARIO, 
                                fontSize: '1.1rem',
                              lineHeight: 1.6,
                                fontWeight: item.isLast ? 'bold' : 'normal'
                              }}
                            >
                              {item.text}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Grid>

                  {/* Indicador VS no centro */}
                  <Grid item xs={12} md={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2, order: { xs: 1, md: 0 } }}>
                    <Box 
                      component={motion.div}
                      animate={{ 
                        scale: [0.9, 1.1, 0.9],
                        opacity: [0.8, 1, 0.8]
                      }}
                      transition={{ 
                        repeat: Infinity,
                        duration: 3,
                        ease: "easeInOut"
                      }}
                      sx={{
                        display: 'flex',
                        backgroundColor: VERDE_MENTA,
                        borderRadius: '50%',
                        width: '60px',
                        height: '60px',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 8px 24px ${withAlpha(VERDE_MENTA, 0.4)}`,
                        border: `3px solid ${FUNDO_PAPEL}`,
                        margin: '0 auto'
                      }}
                    >
                      <Typography variant="h6" sx={{ color: '#000', fontWeight: 'bold', fontSize: '1.2rem' }}>
                        VS
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Card 2: COM O CONEXOR */}
                  <Grid item xs={12} md={12} lg={12} xl={6}>
                    <Box 
                      component={motion.div}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ 
                        delay: 0.8,
                        duration: 0.6
                      }}
                      sx={{
                        backgroundColor: FUNDO_PAPEL,
                        borderRadius: 3,
                        px: 6,
                        py: 3.5,
                        minWidth: '400px',
                        border: `2px solid ${withAlpha(VERDE_MENTA, 0.4)}`,
                        boxShadow: `0 8px 24px ${withAlpha(VERDE_MENTA, 0.2)}`,
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <Typography variant="h6" sx={{ color: VERDE_MENTA, fontWeight: 'bold', mb: 2, textAlign: 'center', fontSize: '1.1rem' }}>
                        ✅ COM O CONEXOR
                      </Typography>
                      
                      <Box sx={{ flex: 1 }}>
                        {[
                          { text: "• Cliente pergunta às 14h", delay: 1.5 },
                          { text: "• CONEXOR responde em 2 segundos", delay: 2.5 },
                          { text: "• IA qualifica e fecha a venda", delay: 3.5 },
                          { text: "• Você não perde NENHUMA venda", delay: 4.5, isLast: true }
                        ].map((item, index) => (
                          <Box 
                            key={index}
                            component={motion.div}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: [0, 1, 1, 0], x: [20, 0, 0, 20] }}
                            transition={{ 
                              repeat: Infinity,
                              duration: 8,
                              delay: item.delay,
                              times: [0, 0.1, 0.9, 1]
                            }}
                            sx={{ mb: 2.5, minHeight: '28px' }}
                          >
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: item.isLast ? VERDE_MENTA : TEXTO_PRIMARIO, 
                                fontSize: '1.1rem',
                              lineHeight: 1.6,
                                fontWeight: item.isLast ? 'bold' : 'normal'
                              }}
                            >
                              {item.text}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                  </Grid>
                </Box>
              </Box>
            </Grid>
          </Grid>
          
          {/* Scroll indicator */}
          <Box 
            component={motion.div}
            animate={{ 
              y: [0, 10, 0]
            }}
            transition={{ 
              repeat: Infinity,
              duration: 1.5,
              ease: "easeInOut"
            }}
            sx={{ 
              position: 'absolute', 
              bottom: -40, 
              left: '50%', 
              transform: 'translateX(-50%)',
              textAlign: 'center',
              cursor: 'pointer'
            }}
            onClick={() => {
              window.scrollTo({
                top: window.innerHeight,
                behavior: 'smooth'
              });
            }}
          >
            <Typography variant="body2" sx={{ color: TEXTO_SECUNDARIO, mb: 1 }}>
              Saiba mais
            </Typography>
            <KeyboardArrowDownIcon sx={{ color: VERDE_MENTA }} />
          </Box>
        </Container>
      </Box>

      {/* === SEÇÃO 2: HISTÓRIA PESSOAL (Autoridade) === */}
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        sx={{
          py: { xs: 8, md: 12 },
          backgroundColor: FUNDO_ESCURO,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center" justifyContent="center">
            <Grid item xs={12} md={10}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2, duration: 0.8 }}
                sx={{ textAlign: 'center' }}
              >
                <Chip
                  component={motion.div}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  label="🚀 EM DESENVOLVIMENTO - VAGAS LIMITADAS"
                  sx={{
                    backgroundColor: withAlpha(VERDE_MENTA, 0.2),
                    color: VERDE_MENTA,
                    fontWeight: 'bold',
                    mb: 4,
                    fontSize: '0.9rem'
                  }}
                />
                
                <Typography
                  component={motion.h2}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                  variant="h3"
                  sx={{
                    fontSize: { xs: '2.2rem', md: '2.8rem' },
                    fontWeight: 'bold',
                    mb: 3,
                    color: TEXTO_PRIMARIO,
                  }}
                >
                  Seja um dos Primeiros a
                  <br />
                  <span style={{ color: VERDE_MENTA }}>Testar o CONEXOR</span>
                </Typography>
                
                <Typography
                  component={motion.p}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 }}
                  variant="h6"
                  sx={{
                    color: TEXTO_SECUNDARIO,
                    mb: 5,
                    lineHeight: 1.5,
                    maxWidth: '600px',
                    mx: 'auto'
                  }}
                >
                  Está chegando! Cadastre-se para ter acesso prioritário ao MVP e ajudar a moldar o futuro da IA para negócios brasileiros.
                </Typography>
                
                <Box 
                  component={motion.div}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.6 }}
                  sx={{ 
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 3,
                    justifyContent: 'center',
                    alignItems: 'center',
                    mb: 4
                  }}
                >
                  <Button 
                    variant="contained" 
                    size="large"
                    onClick={() => window.open('https://wa.me/5585999999999?text=Quero%20participar%20do%20teste%20beta%20do%20CONEXOR', '_blank')}
                    startIcon={<WhatsAppIcon />}
                    sx={{
                      background: `linear-gradient(135deg, ${VERDE_MENTA} 0%, #00ACC1 100%)`,
                      color: '#000',
                      fontWeight: 'bold',
                      px: 4,
                      py: 2,
                      fontSize: '1.1rem',
                      borderRadius: '30px',
                      boxShadow: `0 8px 30px ${withAlpha(VERDE_MENTA, 0.4)}`,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 12px 40px ${withAlpha(VERDE_MENTA, 0.6)}`
                      }
                    }}
                  >
                    📱 Conversar no WhatsApp
                  </Button>
                  
                  <Button 
                    variant="outlined" 
                    size="large"
                    onClick={() => window.open('/demo', '_blank')}
                    startIcon={<PlayArrowIcon />}
                    sx={{
                      borderColor: VERDE_MENTA,
                      color: VERDE_MENTA,
                      fontWeight: 'bold',
                      px: 4,
                      py: 2,
                      fontSize: '1.1rem',
                      borderRadius: '30px',
                      '&:hover': {
                        backgroundColor: withAlpha(VERDE_MENTA, 0.1),
                        borderColor: VERDE_MENTA_CLARO,
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    🎥 Ver Demo (2 min)
                  </Button>
                </Box>
                
                <Typography
                  variant="body2"
                  sx={{
                    color: TEXTO_SECUNDARIO,
                    fontSize: '0.9rem',
                    mb: 3
                  }}
                >
                  🚀 Desenvolvimento com feedback de 50+ empresas beta • ✅ Sem compromisso • 🎆 Acesso prioritário
                </Typography>
                
                <Box sx={{ mt: 4, p: 3, backgroundColor: withAlpha(VERDE_MENTA, 0.1), borderRadius: 2, border: `1px solid ${withAlpha(VERDE_MENTA, 0.3)}` }}>
                  <Typography variant="body2" sx={{ color: VERDE_MENTA, fontWeight: 'bold', mb: 1 }}>
                    🔥 Últimas 20 vagas para teste antecipado
                  </Typography>
                  <Typography variant="body2" sx={{ color: TEXTO_SECUNDARIO }}>
                    Participe da construção do produto e ganhe 6 meses grátis quando lançarmos
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* === SEÇÃO 3: PROBLEMA WHATSAPP + SOLUÇÃO CONEXOR === */}
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        sx={{
          py: { xs: 10, md: 16 },
          background: `radial-gradient(circle at 50% 50%, ${withAlpha(VERDE_MENTA, 0.08)} 0%, transparent 60%)`,
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          {/* Título da seção */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip 
              component={motion.div}
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              label="⚠️ REALIDADE BRUTAL DO WHATSAPP" 
              sx={{ 
                mb: 3, 
                fontWeight: 'bold',
                backgroundColor: withAlpha('#FF6B6B', 0.2),
                color: '#FF6B6B',
                fontSize: '0.9rem'
              }} 
            />
            
            <Typography 
              component={motion.h2}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              variant="h3" 
              sx={{ 
                fontWeight: 'bold', 
                color: TEXTO_PRIMARIO,
                mb: 3,
                fontSize: { xs: '2rem', md: '2.8rem' }
              }}
            >
              Enquanto Você Demora, Seus{' '}
              <span style={{ color: '#FF6B6B' }}>Concorrentes Vendem</span>
            </Typography>
            
            <Typography 
              component={motion.p}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              variant="h6" 
              sx={{ 
                color: TEXTO_SECUNDARIO,
                maxWidth: 800,
                mx: 'auto',
                mb: 6,
                lineHeight: 1.6
              }}
            >
              Mais de 60% dos negócios acontecem inteiramente via WhatsApp. 
              Mas a realidade é devastadora para quem não responde na hora.
            </Typography>
          </Box>

          {/* Estatísticas chocantes */}
          <Grid container spacing={4} sx={{ mb: 8 }}>
            {[
              { 
                number: '15%', 
                label: 'São atendidos no primeiro minuto', 
                desc: 'Apenas 15% dos clientes conseguem resposta imediata',
                color: '#FF6B6B'
              },
              { 
                number: '70%', 
                label: 'Esperam mais de 15 minutos', 
                desc: 'A maioria dos seus clientes está sendo ignorada',
                color: '#FF8A65'
              },
              { 
                number: '60%', 
                label: 'Fazem negócios via WhatsApp', 
                desc: 'É assim que seus clientes preferem comprar',
                color: VERDE_MENTA
              },
            ].map((stat, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box
                  component={motion.div}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 + index * 0.2 }}
                  sx={{
                    backgroundColor: FUNDO_PAPEL,
                    borderRadius: 3,
                    p: 4,
                    textAlign: 'center',
                    border: `2px solid ${withAlpha(stat.color, 0.3)}`,
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: `0 10px 30px ${withAlpha(stat.color, 0.2)}`,
                    }
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      color: stat.color,
                      fontWeight: 'bold',
                      fontSize: '3rem',
                      mb: 2
                    }}
                  >
                    {stat.number}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: TEXTO_PRIMARIO,
                      fontWeight: 'bold',
                      mb: 2
                    }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: TEXTO_SECUNDARIO,
                      lineHeight: 1.5
                    }}
                  >
                    {stat.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Comparativo Antes/Depois */}
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
                sx={{
                  backgroundColor: FUNDO_PAPEL,
                  borderRadius: 3,
                  p: 4,
                  border: `2px solid ${withAlpha('#FF6B6B', 0.3)}`
                }}
              >
                <Typography variant="h5" sx={{ color: '#FF6B6B', fontWeight: 'bold', mb: 3, textAlign: 'center' }}>
                  ❌ SEM O CONEXOR
                </Typography>
                <List sx={{ '& .MuiListItem-root': { py: 1 } }}>
                  <ListItem>
                    <ListItemIcon>
                      <Box sx={{ width: 8, height: 8, backgroundColor: '#FF6B6B', borderRadius: '50%' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Cliente pergunta às 14h" 
                      sx={{ '& .MuiTypography-root': { color: TEXTO_PRIMARIO } }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Box sx={{ width: 8, height: 8, backgroundColor: '#FF6B6B', borderRadius: '50%' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Você vê às 14h20min" 
                      sx={{ '& .MuiTypography-root': { color: TEXTO_PRIMARIO } }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Box sx={{ width: 8, height: 8, backgroundColor: '#FF6B6B', borderRadius: '50%' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Concorrente já vendeu às 14h02min" 
                      sx={{ '& .MuiTypography-root': { color: TEXTO_PRIMARIO } }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Box sx={{ width: 8, height: 8, backgroundColor: '#FF6B6B', borderRadius: '50%' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Você perdeu mais uma venda" 
                      sx={{ '& .MuiTypography-root': { color: '#FF6B6B', fontWeight: 'bold' } }}
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.8 }}
                sx={{
                  backgroundColor: FUNDO_PAPEL,
                  borderRadius: 3,
                  p: 4,
                  border: `2px solid ${withAlpha(VERDE_MENTA, 0.4)}`
                }}
              >
                <Typography variant="h5" sx={{ color: VERDE_MENTA, fontWeight: 'bold', mb: 3, textAlign: 'center' }}>
                  ✅ COM O CONEXOR
                </Typography>
                <List sx={{ '& .MuiListItem-root': { py: 1 } }}>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: VERDE_MENTA, fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Cliente pergunta às 14h" 
                      sx={{ '& .MuiTypography-root': { color: TEXTO_PRIMARIO } }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: VERDE_MENTA, fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="CONEXOR responde em 2 segundos" 
                      sx={{ '& .MuiTypography-root': { color: TEXTO_PRIMARIO } }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: VERDE_MENTA, fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="IA qualifica e fecha a venda" 
                      sx={{ '& .MuiTypography-root': { color: TEXTO_PRIMARIO } }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: VERDE_MENTA, fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Você não perde NENHUMA venda" 
                      sx={{ '& .MuiTypography-root': { color: VERDE_MENTA, fontWeight: 'bold' } }}
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
          </Grid>

          {/* CTA do CONEXOR */}
          <Box 
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 1 }}
            sx={{ textAlign: 'center', mt: 8 }}
          >
            <Typography variant="h4" sx={{ color: TEXTO_PRIMARIO, fontWeight: 'bold', mb: 2 }}>
              Conheça o <span style={{ color: VERDE_MENTA }}>CONEXOR</span>
            </Typography>
            <Typography variant="h6" sx={{ color: TEXTO_SECUNDARIO, mb: 4 }}>
              O assistente de IA que garante que você NUNCA mais perca uma venda por demora
            </Typography>
            <Button 
              component={motion.button}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              variant="contained" 
              size="large" 
              onClick={() => navigate('/register')}
              sx={{
                background: `linear-gradient(135deg, ${VERDE_MENTA} 0%, #00ACC1 100%)`,
                color: '#000',
                fontWeight: 'bold',
                fontSize: '1.2rem',
                px: 6,
                py: 2,
                borderRadius: '30px',
                '&:hover': {
                  background: `linear-gradient(135deg, ${VERDE_MENTA_CLARO} 0%, #26C6DA 100%)`,
                }
              }}
            >
              🚀 Quero o CONEXOR Funcionando Agora
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Box 
        component={motion.div}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        sx={{ 
          py: { xs: 8, md: 12 }, 
          backgroundColor: FUNDO_ESCURO,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background decoration */}
        <Box 
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            background: `linear-gradient(135deg, transparent 0%, ${withAlpha(VERDE_MENTA, 0.03)} 100%)`,
            clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
            zIndex: 0
          }}
        />
        
        <Container sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Chip 
              component={motion.div}
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              label="Recursos Poderosos" 
              color="primary" 
              icon={<BoltIcon />} 
              sx={{ 
                mb: 3, 
                fontWeight: 'bold',
                backgroundColor: withAlpha(VERDE_MENTA, 0.2),
                color: VERDE_MENTA,
                '& .MuiChip-icon': { color: VERDE_MENTA }
              }} 
            />
            
            <Typography 
              component={motion.h2}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              variant="h3" 
              gutterBottom 
              sx={{ 
                fontWeight: 'bold', 
                color: TEXTO_PRIMARIO,
                mb: 2
              }}
            >
              Tudo que você precisa para automatizar seu atendimento
            </Typography>
            
            <Typography 
              component={motion.p}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              variant="h6" 
              sx={{ 
                color: TEXTO_SECUNDARIO,
                maxWidth: 700,
                mx: 'auto',
                mb: 6
              }}
            >
              Nossa plataforma oferece recursos avançados de IA com uma interface simples e intuitiva
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card 
                  component={motion.div}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  whileHover={{ 
                    y: -8,
                    boxShadow: `0 12px 28px ${withAlpha(VERDE_MENTA, 0.2)}`,
                    borderColor: VERDE_MENTA
                  }}
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)', 
                    backgroundColor: FUNDO_PAPEL,
                    border: `1px solid ${withAlpha(VERDE_MENTA, 0.2)}`,
                    boxShadow: `0 8px 24px ${withAlpha(VERDE_MENTA, 0.1)}`,
                    borderRadius: '16px',
                    overflow: 'hidden'
                  }}
                >
                  <Box 
                    sx={{ 
                      p: 3, 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center',
                      position: 'relative',
                      zIndex: 1
                    }}
                  >
                    <Box 
                      sx={{ 
                        mb: 3,
                        px: 6,
                        py: 3.5,
                        minWidth: '400px', 
                        borderRadius: '50%', 
                        bgcolor: withAlpha(VERDE_MENTA, 0.15),
                        color: VERDE_MENTA,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        transition: '0.3s',
                        transform: 'scale(1)',
                        '&:hover': {
                          transform: 'scale(1.1)',
                          bgcolor: withAlpha(VERDE_MENTA, 0.25),
                        }
                      }}
                    >
                      {feature.icon}
                    </Box>
                    
                    <Typography 
                      gutterBottom 
                      variant="h6" 
                      component="h3" 
                      align="center" 
                      sx={{ 
                        color: TEXTO_PRIMARIO,
                        fontWeight: 'bold',
                        mb: 2
                      }}
                    >
                      {feature.title}
                    </Typography>
                    
                    <Typography 
                      variant="body2" 
                      align="center" 
                      sx={{ 
                        color: TEXTO_SECUNDARIO,
                        lineHeight: 1.6
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Box>
                  
                  {/* Decorative element */}
                  <Box 
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '80px',
                      height: '80px',
                      background: withAlpha(VERDE_MENTA, 0.05),
                      borderRadius: '0 0 0 100%',
                      zIndex: 0
                    }}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
          
          {/* Additional feature highlight */}
          <Box 
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            sx={{ 
              mt: 8, 
              p: 4, 
              borderRadius: '24px',
              backgroundColor: withAlpha(VERDE_MENTA, 0.05),
              border: `1px solid ${withAlpha(VERDE_MENTA, 0.2)}`,
              boxShadow: `0 8px 32px ${withAlpha(VERDE_MENTA, 0.1)}`,
            }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: TEXTO_PRIMARIO }}>
                  Modo Seletivo Inteligente
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: TEXTO_SECUNDARIO, mb: 3 }}>
                  Nossos agentes decidem autonomamente quando devem responder, ignorando mensagens irrelevantes e focando apenas no que importa para seu negócio.
                </Typography>
                
                <List>
                  {[
                    'Avaliação de contexto e histórico de interações',
                    'Configuração personalizada por tipo de mensagem',
                    'Filtragem inteligente baseada em regras',
                    'Escalação automática para humanos quando necessário'
                  ].map((item, idx) => (
                    <ListItem key={idx} sx={{ p: 0, mb: 1 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckIcon sx={{ color: VERDE_MENTA }} />
                      </ListItemIcon>
                      <ListItemText primary={item} sx={{ color: TEXTO_SECUNDARIO }} />
                    </ListItem>
                  ))}
                </List>
                
                <Button 
                  component={motion.button}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  variant="contained" 
                  size="large"
                  endIcon={<AutoAwesomeIcon />}
                  sx={{
                    mt: 3,
                    backgroundColor: VERDE_MENTA,
                    color: '#000',
                    fontWeight: 'bold',
                    px: 3,
                    py: 1.5,
                    borderRadius: '30px',
                    '&:hover': {
                      backgroundColor: VERDE_MENTA_CLARO
                    }
                  }}
                  onClick={() => navigate('/features')}
                >
                  Conheça todos os recursos
                </Button>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box 
                  component="img"
                  src="/images/selective-mode-illustration.png"
                  alt="Modo Seletivo Inteligente"
                  sx={{
                    width: '100%',
                    maxWidth: 500,
                    height: 'auto',
                    borderRadius: '16px',
                    boxShadow: `0 20px 40px ${withAlpha('#000', 0.2)}`,
                    border: `1px solid ${withAlpha(VERDE_MENTA, 0.3)}`
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box 
        component={motion.div}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        sx={{ 
          py: { xs: 10, md: 14 }, 
          backgroundColor: FUNDO_DRAWER,
          backgroundImage: `linear-gradient(135deg, ${withAlpha(VERDE_MENTA, 0.05)} 0%, transparent 100%)`,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background decoration */}
        <Box 
          sx={{
            position: 'absolute',
            top: '10%',
            left: 0,
            width: '30%',
            height: '60%',
            background: `radial-gradient(circle, ${withAlpha(VERDE_MENTA, 0.08)} 0%, transparent 70%)`,
            zIndex: 0
          }}
        />
        <Box 
          sx={{
            position: 'absolute',
            bottom: '5%',
            right: 0,
            width: '40%',
            height: '50%',
            background: `radial-gradient(circle, ${withAlpha(VERDE_MENTA, 0.05)} 0%, transparent 70%)`,
            zIndex: 0
          }}
        />
        
        <Container sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 10 } }}>
            <Chip 
              component={motion.div}
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              label="Processo Simples" 
              color="primary" 
              icon={<SpeedIcon />} 
              sx={{ 
                mb: 3, 
                fontWeight: 'bold',
                backgroundColor: withAlpha(VERDE_MENTA, 0.2),
                color: VERDE_MENTA,
                '& .MuiChip-icon': { color: VERDE_MENTA }
              }} 
            />
            
            <Typography 
              component={motion.h2}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              variant="h3" 
              gutterBottom 
              sx={{ 
                fontWeight: 'bold', 
                color: TEXTO_PRIMARIO,
                mb: 2
              }}
            >
              Comece a usar em minutos, não em semanas
            </Typography>
            
            <Typography 
              component={motion.p}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              variant="h6" 
              sx={{ 
                color: TEXTO_SECUNDARIO,
                maxWidth: 700,
                mx: 'auto',
                mb: 2
              }}
            >
              Automatize seu atendimento no WhatsApp em três passos simples
            </Typography>
          </Box>
          
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box 
                component={motion.div}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                sx={{ p: 4 }}
              >
                <Typography variant="h5" component="h3" gutterBottom sx={{ mb: 3, fontWeight: 'bold', color: TEXTO_PRIMARIO }}>
                  Crie agentes inteligentes em minutos
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Avatar sx={{ 
                        bgcolor: withAlpha(VERDE_MENTA, 0.2), 
                        color: VERDE_MENTA,
                        fontWeight: 'bold',
                        border: `2px solid ${VERDE_MENTA}`,
                        boxShadow: `0 4px 8px ${withAlpha(VERDE_MENTA, 0.3)}`
                      }}>1</Avatar>
                    </ListItemIcon>
                    <Box sx={{ ml: 2 }}>
                      <Typography sx={{ color: TEXTO_PRIMARIO, fontWeight: 500, mb: 1 }}>Conecte múltiplos canais</Typography>
                      <Box sx={{ color: TEXTO_SECUNDARIO }}>Integre facilmente com WhatsApp, Instagram, seu site e outros canais de comunicação em minutos.</Box>
                    </Box>
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Avatar sx={{ 
                        bgcolor: withAlpha(VERDE_MENTA, 0.2), 
                        color: VERDE_MENTA,
                        fontWeight: 'bold',
                        border: `2px solid ${VERDE_MENTA}`,
                        boxShadow: `0 4px 8px ${withAlpha(VERDE_MENTA, 0.3)}`
                      }}>2</Avatar>
                    </ListItemIcon>
                    <Box sx={{ ml: 2 }}>
                      <Typography sx={{ color: TEXTO_PRIMARIO, fontWeight: 500, mb: 1 }}>Alimente seu Agente com conhecimento</Typography>
                      <Box sx={{ color: TEXTO_SECUNDARIO }}>Forneça catálogos, agendas, textos, manuais, vídeos e sites para seu agente aprender sobre seu negócio e atender melhor seus clientes.</Box>
                    </Box>
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Avatar sx={{ 
                        bgcolor: withAlpha(VERDE_MENTA, 0.2), 
                        color: VERDE_MENTA,
                        fontWeight: 'bold',
                        border: `2px solid ${VERDE_MENTA}`,
                        boxShadow: `0 4px 8px ${withAlpha(VERDE_MENTA, 0.3)}`
                      }}>3</Avatar>
                    </ListItemIcon>
                    <Box sx={{ ml: 2 }}>
                      <Typography sx={{ color: TEXTO_PRIMARIO, fontWeight: 500, mb: 1 }}>Eleve a experiência de relacionamento</Typography>
                      <Box sx={{ color: TEXTO_SECUNDARIO, mb: 1 }}>Melhore o relacionamento com clientes, fornecedores e público interno através de atendimento personalizado e inteligente em todos os canais.</Box>
                          <Box 
                            component={motion.div}
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.3 }}
                            sx={{ 
                              p: 2, 
                              bgcolor: alpha('#000', 0.7),
                              borderRadius: 2,
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              color: '#00FF00',
                              maxWidth: '100%',
                              overflow: 'hidden',
                              position: 'relative',
                              mt: 2,
                              border: '1px solid #333',
                              boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
                            }}
                          >
                            <motion.div
                              initial={{ width: 0 }}
                              whileInView={{ width: '100%' }}
                              viewport={{ once: true }}
                              transition={{ 
                                duration: 3, 
                                ease: "easeInOut"
                              }}
                            >
                              <Box sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre', color: '#00FF00' }}>
                                {`// Exemplo de integração com webhook\nconst webhook = {\n  url: 'https://api.neuroagentes.com/webhook',\n  headers: { 'Authorization': 'Bearer ${'{TOKEN}'}' },\n  method: 'POST',\n  body: JSON.stringify({\n    message: message,\n    customer: customer,\n    agent: 'sales-assistant'\n  })\n};\n\n// Enviar dados para o webhook\nfetch(webhook.url, {\n  method: webhook.method,\n  headers: webhook.headers,\n  body: webhook.body\n})\n.then(response => response.json())\n.then(data => console.log('Resposta do NeuroAgente:', data));`}
                              </Box>
                            </motion.div>
                            <motion.div 
                              style={{ 
                                position: 'absolute', 
                                top: 0, 
                                left: 0, 
                                height: '100%', 
                                width: '100%', 
                                background: 'linear-gradient(90deg, transparent 0%, transparent 50%, #000 100%)',
                                opacity: 0.5
                              }}
                              initial={{ x: '-100%' }}
                              whileInView={{ x: '100%' }}
                              viewport={{ once: true }}
                              transition={{ 
                                duration: 3, 
                                ease: "easeInOut",
                                repeat: Infinity,
                                repeatDelay: 1
                              }}
                            />
                          </Box>
                        </Box>
                  </ListItem>
                </List>
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <Button 
                    component={motion.button}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    variant="contained" 
                    size="large"
                    onClick={() => navigate('/register')}
                    sx={{
                      backgroundColor: VERDE_MENTA,
                      color: '#000',
                      fontWeight: 'bold',
                      px: 4,
                      py: 1.5,
                      borderRadius: '30px',
                      boxShadow: `0 8px 16px ${withAlpha(VERDE_MENTA, 0.3)}`,
                      '&:hover': {
                        backgroundColor: VERDE_MENTA_CLARO,
                        boxShadow: `0 12px 20px ${withAlpha(VERDE_MENTA, 0.4)}`
                      }
                    }}
                  >
                    🔔 Notificar Lançamento
                  </Button>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
              >
                <Box
                  component="img"
                  src="/img/integration-diagram.png"
                  alt="Diagrama de Integração"
                  sx={{
                    width: '100%',
                    maxWidth: 500,
                    display: 'block',
                    mx: 'auto',
                    borderRadius: '16px',
                    boxShadow: `0 20px 40px ${withAlpha('#000', 0.2)}`,
                    border: `1px solid ${withAlpha(VERDE_MENTA, 0.3)}`
                  }}
                />
              </Box>
            </Grid>
          </Grid>
          
          <Box 
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            sx={{ 
              textAlign: 'center', 
              mt: 8
            }}
          >
            <Typography variant="body2" sx={{ color: TEXTO_SECUNDARIO, mb: 2 }}>
              Sem cartão de crédito • Configuração em minutos • Suporte gratuito
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box 
        component={motion.div}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        sx={{ 
          py: { xs: 10, md: 14 }, 
          backgroundColor: FUNDO_ESCURO,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background decoration */}
        <Box 
          sx={{
            position: 'absolute',
            top: '5%',
            left: '5%',
            width: '90%',
            height: '90%',
            background: `radial-gradient(circle at 70% 20%, ${withAlpha(VERDE_MENTA, 0.08)} 0%, transparent 60%)`,
            zIndex: 0
          }}
        />
        
        <Container sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 10 } }}>
            <Chip 
              component={motion.div}
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              label="Preços Transparentes" 
              color="primary" 
              icon={<AttachMoneyIcon />} 
              sx={{ 
                mb: 3, 
                fontWeight: 'bold',
                backgroundColor: withAlpha(VERDE_MENTA, 0.2),
                color: VERDE_MENTA,
                '& .MuiChip-icon': { color: VERDE_MENTA }
              }} 
            />
            
            <Typography 
              component={motion.h2}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              variant="h3" 
              gutterBottom 
              sx={{ 
                fontWeight: 'bold', 
                color: TEXTO_PRIMARIO,
                mb: 2
              }}
            >
              Planos que crescem com seu negócio
            </Typography>
            
            <Typography 
              component={motion.p}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              variant="h6" 
              sx={{ 
                color: TEXTO_SECUNDARIO,
                maxWidth: 700,
                mx: 'auto',
                mb: 6
              }}
            >
              Escolha o plano ideal para o seu negócio, sem surpresas ou taxas ocultas
            </Typography>
          </Box>
          
          <Grid container spacing={4} justifyContent="center">
            {plans.map((plan, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper 
                  component={motion.div}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + index * 0.2 }}
                  whileHover={{ 
                    y: -10,
                    boxShadow: `0 20px 40px ${withAlpha('#000', 0.3)}`,
                    borderColor: VERDE_MENTA
                  }}
                  elevation={0} 
                  sx={{ 
                    p: 3, 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '16px',
                    border: plan.highlighted ? `2px solid ${VERDE_MENTA}` : `1px solid ${withAlpha(VERDE_MENTA, 0.2)}`,
                    backgroundColor: FUNDO_PAPEL,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    boxShadow: plan.highlighted ? `0 8px 24px ${withAlpha(VERDE_MENTA, 0.2)}` : `0 4px 12px ${withAlpha('#000', 0.2)}`,
                    transform: plan.highlighted ? 'scale(1.05)' : 'none',
                    '&:hover': {
                      transform: plan.highlighted ? 'scale(1.08)' : 'scale(1.03)',
                      boxShadow: plan.highlighted ? `0 12px 28px ${withAlpha(VERDE_MENTA, 0.3)}` : `0 8px 16px ${withAlpha(VERDE_MENTA, 0.15)}`,
                      borderColor: plan.highlighted ? VERDE_MENTA : withAlpha(VERDE_MENTA, 0.4),
                      zIndex: 1
                    }
                  }}
                >
                  {/* Decorative element */}
                  <Box 
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '150px',
                      height: '150px',
                      background: `radial-gradient(circle at top right, ${withAlpha(VERDE_MENTA, plan.highlighted ? 0.2 : 0.05)} 0%, transparent 70%)`,
                      zIndex: 0
                    }}
                  />
                  
                  {plan.highlighted && (
                    <Box
                      component={motion.div}
                      initial={{ x: 100 }}
                      animate={{ x: 0 }}
                      transition={{ delay: 0.5, type: "spring", stiffness: 100 }}
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: VERDE_MENTA,
                        color: '#000',
                        px: 2,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                        zIndex: 2
                      }}
                    >
                      MAIS POPULAR
                    </Box>
                  )}
                  
                  <Box sx={{ mb: 3, textAlign: 'center', position: 'relative', zIndex: 1 }}>
                    <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', color: TEXTO_PRIMARIO }}>
                      {plan.title}
                    </Typography>
                    <Typography variant="h4" component="div" sx={{ mt: 2, fontWeight: 'bold', color: plan.highlighted ? VERDE_MENTA : TEXTO_PRIMARIO }}>
                      {plan.price}
                    </Typography>
                    {plan.originalPrice && (
                      <Typography variant="body2" sx={{ color: TEXTO_SECUNDARIO, textDecoration: 'line-through', mt: 0.5 }}>
                        Preço previsto: {plan.originalPrice}
                      </Typography>
                    )}
                  </Box>
                  
                  <Divider sx={{ mb: 3, borderColor: withAlpha(VERDE_MENTA, 0.2) }} />
                  
                  <Box sx={{ flexGrow: 1, position: 'relative', zIndex: 1 }}>
                    <List>
                      {plan.features.map((feature, idx) => (
                        <ListItem key={idx} dense sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckIcon sx={{ color: VERDE_MENTA }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={<Typography sx={{ color: TEXTO_PRIMARIO, fontWeight: 'medium' }}>{feature}</Typography>} 
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                  
                  <Box sx={{ textAlign: 'center', mt: 3, position: 'relative', zIndex: 1 }}>
                    <Button 
                      component={motion.button}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      variant={plan.highlighted ? "contained" : "outlined"}
                      fullWidth
                      size="large"
                      endIcon={plan.highlighted ? <ArrowForwardIcon /> : null}
                      onClick={() => navigate('/register')}
                      sx={{
                        ...(plan.highlighted ? {
                          backgroundColor: VERDE_MENTA,
                          color: '#000',
                          fontWeight: 'bold',
                          borderRadius: '30px',
                          boxShadow: `0 8px 16px ${withAlpha(VERDE_MENTA, 0.3)}`,
                          '&:hover': {
                            backgroundColor: VERDE_MENTA_CLARO,
                            boxShadow: `0 12px 20px ${withAlpha(VERDE_MENTA, 0.4)}`
                          }
                        } : {
                          color: VERDE_MENTA,
                          borderColor: VERDE_MENTA,
                          borderRadius: '30px',
                          '&:hover': {
                            borderColor: VERDE_MENTA_CLARO,
                            backgroundColor: withAlpha(VERDE_MENTA, 0.1)
                          }
                        })
                      }}
                    >
                      {plan.highlighted ? '🔔 Ser Notificado' : '📋 Lista de Espera'}
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
          
          <Box 
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.8 }}
            sx={{ 
              mt: 8, 
              p: 4, 
              borderRadius: '16px',
              backgroundColor: withAlpha(VERDE_MENTA, 0.05),
              border: `1px solid ${withAlpha(VERDE_MENTA, 0.1)}`,
              textAlign: 'center'
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: TEXTO_PRIMARIO, fontWeight: 'bold' }}>
              Precisa de um plano personalizado para sua empresa?
            </Typography>
            <Typography variant="body1" paragraph sx={{ color: TEXTO_SECUNDARIO, maxWidth: 700, mx: 'auto', mb: 3 }}>
              Entre em contato com nossa equipe para uma solução sob medida que atenda às necessidades específicas do seu negócio.
            </Typography>
            <Button 
              component={motion.button}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              variant="outlined" 
              size="large"
              startIcon={<BusinessIcon />}
              sx={{ 
                borderColor: VERDE_MENTA,
                color: VERDE_MENTA,
                fontWeight: 'bold',
                borderRadius: '30px',
                px: 3,
                '&:hover': {
                  backgroundColor: withAlpha(VERDE_MENTA, 0.1),
                  borderColor: VERDE_MENTA
                }
              }}
              onClick={() => navigate('/contact')}
            >
              Fale com um Consultor
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Developers Section */}
      <Box 
        component={motion.div}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        sx={{ 
          py: { xs: 10, md: 14 }, 
          backgroundColor: '#1A1A2E', // Cor de fundo diferente para a seção de desenvolvedores
          backgroundImage: `linear-gradient(135deg, #1A1A2E 0%, #0F3460 100%)`,
          position: 'relative',
          overflow: 'hidden',
          color: '#fff'
        }}
      >
        {/* Background decoration */}
        <Box 
          sx={{
            position: 'absolute',
            top: '10%',
            left: 0,
            width: '30%',
            height: '60%',
            background: `radial-gradient(circle, ${withAlpha('#4361EE', 0.2)} 0%, transparent 70%)`,
            zIndex: 0
          }}
        />
        <Box 
          sx={{
            position: 'absolute',
            bottom: '5%',
            right: 0,
            width: '40%',
            height: '50%',
            background: `radial-gradient(circle, ${withAlpha('#4361EE', 0.15)} 0%, transparent 70%)`,
            zIndex: 0
          }}
        />
        
        <Container sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 10 } }}>
            <Chip 
              component={motion.div}
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              label="Para Desenvolvedores" 
              icon={<CodeIcon />} 
              sx={{ 
                mb: 3, 
                fontWeight: 'bold',
                backgroundColor: withAlpha('#4361EE', 0.2),
                color: '#4CC9F0',
                '& .MuiChip-icon': { color: '#4CC9F0' }
              }} 
            />
            
            <Typography 
              component={motion.h2}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              variant="h3" 
              gutterBottom 
              sx={{ 
                fontWeight: 'bold', 
                color: '#fff',
                mb: 2
              }}
            >
              Potencialize suas aplicações com nossa API
            </Typography>
            
            <Typography 
              component={motion.p}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              variant="h6" 
              sx={{ 
                color: '#B8C1EC',
                maxWidth: 700,
                mx: 'auto',
                mb: 6
              }}
            >
              Integre facilmente com nossos webhooks e APIs para criar experiências personalizadas
            </Typography>
          </Box>
          
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box 
                component={motion.div}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                sx={{ p: 4 }}
              >
                <Typography variant="h5" component="h3" gutterBottom sx={{ mb: 3, fontWeight: 'bold', color: '#fff' }}>
                  Recursos para Desenvolvedores
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Avatar sx={{ 
                        bgcolor: withAlpha('#4361EE', 0.2), 
                        color: '#4CC9F0',
                        fontWeight: 'bold',
                        border: `2px solid #4CC9F0`,
                        boxShadow: `0 4px 8px ${withAlpha('#4361EE', 0.3)}`
                      }}><WebhookIcon /></Avatar>
                    </ListItemIcon>
                    <Box sx={{ ml: 2 }}>
                      <Typography sx={{ color: '#fff', fontWeight: 500, mb: 1 }}>Webhooks Inteligentes</Typography>
                      <Box sx={{ color: '#B8C1EC' }}>Integre facilmente com seu sistema existente e defina quando a IA deve intervir nas conversas.</Box>
                    </Box>
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Avatar sx={{ 
                        bgcolor: withAlpha('#4361EE', 0.2), 
                        color: '#4CC9F0',
                        fontWeight: 'bold',
                        border: `2px solid #4CC9F0`,
                        boxShadow: `0 4px 8px ${withAlpha('#4361EE', 0.3)}`
                      }}><ApiIcon /></Avatar>
                    </ListItemIcon>
                    <Box sx={{ ml: 2 }}>
                      <Typography sx={{ color: '#fff', fontWeight: 500, mb: 1 }}>API Completa</Typography>
                      <Box sx={{ color: '#B8C1EC' }}>Acesse todos os recursos da plataforma programaticamente com nossa API RESTful documentada.</Box>
                    </Box>
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Avatar sx={{ 
                        bgcolor: withAlpha('#4361EE', 0.2), 
                        color: '#4CC9F0',
                        fontWeight: 'bold',
                        border: `2px solid #4CC9F0`,
                        boxShadow: `0 4px 8px ${withAlpha('#4361EE', 0.3)}`
                      }}><IntegrationInstructionsIcon /></Avatar>
                    </ListItemIcon>
                    <Box sx={{ ml: 2 }}>
                      <Typography sx={{ color: '#fff', fontWeight: 500, mb: 1 }}>SDKs e Bibliotecas</Typography>
                      <Box sx={{ color: '#B8C1EC' }}>Utilize nossas bibliotecas para as principais linguagens de programação e frameworks.</Box>
                    </Box>
                  </ListItem>
                </List>
                <Box sx={{ mt: 4 }}>
                  <Button 
                    component={motion.button}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    variant="contained" 
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    sx={{ 
                      backgroundColor: '#4CC9F0',
                      color: '#1A1A2E',
                      fontWeight: 'bold',
                      borderRadius: '30px',
                      px: 3,
                      py: 1.5,
                      '&:hover': {
                        backgroundColor: '#7BE0FF',
                      }
                    }}
                    onClick={() => navigate('/developers')}
                  >
                    Explorar Documentação
                  </Button>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box 
                component={motion.div}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                sx={{ 
                  p: 3, 
                  borderRadius: '12px',
                  backgroundColor: withAlpha('#000', 0.3),
                  border: `1px solid ${withAlpha('#4CC9F0', 0.3)}`,
                  boxShadow: `0 8px 32px ${withAlpha('#000', 0.3)}`,
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <Box 
                  component={motion.div}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.7 }}
                  sx={{ 
                    p: 2, 
                    bgcolor: '#0D1B2A',
                    borderRadius: 2,
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    color: '#4CC9F0',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    position: 'relative',
                    border: '1px solid #1B263B',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
                  }}
                >
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: '100%' }}
                    viewport={{ once: true }}
                    transition={{ 
                      duration: 3, 
                      ease: "easeInOut"
                    }}
                  >
                    <Box sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre', color: '#4CC9F0' }}>
                      {`// Exemplo de integração com a API
const NeuroAgentes = require('neuroagentes-sdk');

// Inicialize o cliente com sua chave de API
const client = new NeuroAgentes.Client({
  apiKey: 'sua-chave-api',
  environment: 'production'
});

// Crie um novo agente
async function createAgent() {
  const agent = await client.agents.create({
    name: 'Assistente de Vendas',
    description: 'Agente especializado em vendas',
    documents: [
      { type: 'catalog', url: 'https://example.com/catalog.pdf' },
      { type: 'manual', content: 'Informações sobre produtos...' }
    ],
    channels: ['whatsapp', 'website', 'instagram']
  });
  
  console.log('Agente criado com sucesso:', agent.id);
  return agent;
}`}
                    </Box>
                  </motion.div>
                  <motion.div 
                    style={{ 
                      position: 'absolute', 
                      top: 0, 
                      left: 0, 
                      height: '100%', 
                      width: '100%', 
                      background: `linear-gradient(to bottom, transparent 50%, ${withAlpha('#0D1B2A', 0.8)} 100%)`,
                      pointerEvents: 'none'
                    }}
                  />
                </Box>
                
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
                  {['JavaScript', 'Python', 'PHP', 'Ruby', 'Go'].map((lang, index) => (
                    <Chip 
                      key={index}
                      label={lang} 
                      size="small"
                      sx={{ 
                        backgroundColor: withAlpha('#4361EE', 0.1),
                        color: '#B8C1EC',
                        border: `1px solid ${withAlpha('#4361EE', 0.3)}`
                      }} 
                    />
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box 
        component={motion.div}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        sx={{ 
          py: { xs: 10, md: 14 }, 
          background: `linear-gradient(135deg, ${FUNDO_ESCURO} 0%, ${withAlpha(VERDE_MENTA, 0.3)} 100%)`,
          position: 'relative',
          overflow: 'hidden',
          border: `1px solid ${withAlpha(VERDE_MENTA, 0.2)}`,
          borderLeft: 0,
          borderRight: 0
        }}
      >
        {/* Animated background elements */}
        <Box 
          component={motion.div}
          animate={{ 
            y: [0, -15, 0],
            opacity: [0.5, 0.8, 0.5]
          }}
          transition={{ 
            repeat: Infinity, 
            duration: 5,
            ease: "easeInOut" 
          }}
          sx={{
            position: 'absolute',
            top: '20%',
            left: '10%',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${withAlpha(VERDE_MENTA, 0.2)} 0%, transparent 70%)`,
            filter: 'blur(40px)',
            zIndex: 0
          }}
        />
        
        <Box 
          component={motion.div}
          animate={{ 
            y: [0, 20, 0],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ 
            repeat: Infinity, 
            duration: 7,
            ease: "easeInOut",
            delay: 1
          }}
          sx={{
            position: 'absolute',
            bottom: '10%',
            right: '15%',
            width: '250px',
            height: '250px',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${withAlpha(VERDE_MENTA, 0.15)} 0%, transparent 70%)`,
            filter: 'blur(30px)',
            zIndex: 0
          }}
        />
        
        <Container sx={{ position: 'relative', zIndex: 1 }}>
          <Grid 
            container 
            spacing={4} 
            alignItems="center" 
            justifyContent="center"
          >
            <Grid item xs={12} md={10} lg={8}>
              <Box 
                sx={{ 
                  textAlign: 'center',
                  p: { xs: 3, md: 6 },
                  borderRadius: '24px',
                  backgroundColor: withAlpha('#000', 0.3),
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${withAlpha(VERDE_MENTA, 0.3)}`,
                  boxShadow: `0 20px 40px ${withAlpha('#000', 0.3)}`
                }}
              >
                <Typography 
                  component={motion.h2}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  variant="h3" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: TEXTO_PRIMARIO,
                    mb: 3,
                    textShadow: `0 0 10px ${withAlpha(VERDE_MENTA, 0.3)}`
                  }}
                >
                  Pronto para revolucionar seu atendimento?
                </Typography>
                
                <Typography 
                  component={motion.p}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  variant="h6" 
                  sx={{ 
                    mb: 5, 
                    color: TEXTO_SECUNDARIO,
                    maxWidth: 700,
                    mx: 'auto'
                  }}
                >
                  Junte-se a centenas de empresas que já estão economizando tempo e recursos com nossos agentes de IA personalizados.
                </Typography>
                
                <Box 
                  component={motion.div}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                  sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' }, 
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 2
                  }}
                >
                  <Button 
                    component={motion.button}
                    whileHover={{ 
                      scale: 1.05,
                      boxShadow: `0 12px 25px ${withAlpha(VERDE_MENTA, 0.5)}`
                    }}
                    whileTap={{ scale: 0.98 }}
                    variant="contained" 
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    sx={{ 
                      py: 1.8,
                      px: 4,
                      backgroundColor: VERDE_MENTA,
                      color: '#000',
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      borderRadius: '30px',
                      boxShadow: `0 8px 20px ${withAlpha(VERDE_MENTA, 0.4)}`,
                      '&:hover': {
                        backgroundColor: VERDE_MENTA_CLARO,
                        transform: 'translateY(-3px)',
                        boxShadow: `0 12px 25px ${withAlpha(VERDE_MENTA, 0.5)}`
                      }
                    }}
                    onClick={() => navigate('/register')}
                  >
                    Começar Gratuitamente
                  </Button>
                  
                  <Button 
                    component={motion.button}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    variant="outlined" 
                    size="large"
                    startIcon={<PlayArrowIcon />}
                    sx={{ 
                      py: 1.8,
                      px: 4,
                      borderColor: withAlpha(VERDE_MENTA, 0.5),
                      color: TEXTO_PRIMARIO,
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      borderRadius: '30px',
                      '&:hover': {
                        borderColor: VERDE_MENTA,
                        backgroundColor: withAlpha(VERDE_MENTA, 0.1),
                        transform: 'translateY(-3px)'
                      }
                    }}
                    onClick={() => window.open('https://www.youtube.com/watch?v=dQw4w9WgXcQ', '_blank')}
                  >
                    Ver Demonstração
                  </Button>
                </Box>
                
                <Box 
                  component={motion.div}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 }}
                  sx={{ 
                    mt: 5,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: { xs: 2, sm: 4 }
                  }}
                >
                  {[
                    { icon: <SecurityIcon sx={{ color: VERDE_MENTA }}/>, text: "Segurança de nível empresarial" },
                    { icon: <SpeedIcon sx={{ color: VERDE_MENTA }}/>, text: "Configuração em minutos" },
                    { icon: <SupportAgentIcon sx={{ color: VERDE_MENTA }}/>, text: "Suporte técnico 24/7" }
                  ].map((item, index) => (
                    <Box 
                      key={index}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      {item.icon}
                      <Typography variant="body2" sx={{ color: TEXTO_SECUNDARIO }}>
                        {item.text}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box 
        component={motion.footer}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        sx={{ 
          py: { xs: 8, md: 10 }, 
          backgroundColor: FUNDO_ESCURO, 
          color: TEXTO_PRIMARIO,
          position: 'relative',
          overflow: 'hidden',
          borderTop: `1px solid ${withAlpha(VERDE_MENTA, 0.2)}`
        }}
      >
        {/* Animated background elements */}
        <Box 
          component={motion.div}
          animate={{ 
            x: [0, -15, 0],
            opacity: [0.2, 0.3, 0.2]
          }}
          transition={{ 
            repeat: Infinity, 
            duration: 8,
            ease: "easeInOut" 
          }}
          sx={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${withAlpha(VERDE_MENTA, 0.1)} 0%, transparent 70%)`,
            filter: 'blur(40px)',
            zIndex: 0
          }}
        />
        
        <Container sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4}>
            <Grid 
              item 
              xs={12} 
              md={4}
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    mb: 2,
                    fontWeight: 'bold',
                    background: `linear-gradient(90deg, ${VERDE_MENTA} 0%, ${VERDE_MENTA_CLARO} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    display: 'inline-block'
                  }}
                >
                  NeuroAgentes
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: theme.palette.grey[400],
                    mb: 3,
                    maxWidth: 300
                  }}
                >
                  Transformando o atendimento ao cliente com a mais avançada IA e integração com WhatsApp.
                </Typography>
                
                {/* Social Media Icons */}
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  {[
                    { icon: <FacebookIcon />, label: 'Facebook' },
                    { icon: <TwitterIcon />, label: 'Twitter' },
                    { icon: <LinkedInIcon />, label: 'LinkedIn' },
                    { icon: <InstagramIcon />, label: 'Instagram' }
                  ].map((social, index) => (
                    <IconButton 
                      key={index}
                      component={motion.button}
                      whileHover={{ 
                        scale: 1.2, 
                        backgroundColor: withAlpha(VERDE_MENTA, 0.2),
                        color: VERDE_MENTA
                      }}
                      whileTap={{ scale: 0.95 }}
                      aria-label={social.label}
                      sx={{ 
                        color: theme.palette.grey[400],
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          color: VERDE_MENTA
                        }
                      }}
                    >
                      {social.icon}
                    </IconButton>
                  ))}
                </Box>
              </Box>
            </Grid>
            
            {[
              { 
                title: 'Empresa', 
                links: ['Sobre Nós', 'Carreiras', 'Blog', 'Parceiros']
              },
              { 
                title: 'Recursos', 
                links: ['Documentação', 'API', 'Status', 'Tutoriais']
              },
              { 
                title: 'Legal', 
                links: ['Termos', 'Privacidade', 'LGPD', 'Cookies']
              },
              { 
                title: 'Suporte', 
                links: ['Ajuda', 'Contato', 'FAQ', 'Comunidade']
              }
            ].map((section, sectionIndex) => (
              <Grid 
                key={sectionIndex} 
                item 
                xs={6} 
                sm={6} 
                md={2}
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 + (sectionIndex * 0.1) }}
              >
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    mb: 3,
                    fontWeight: 'bold',
                    color: theme.palette.grey[300]
                  }}
                >
                  {section.title}
                </Typography>
                <List dense disablePadding>
                  {section.links.map((link, linkIndex) => (
                    <ListItem 
                      key={linkIndex} 
                      disablePadding 
                      sx={{ pb: 1.2 }}
                      component={motion.li}
                      whileHover={{ x: 5 }}
                    >
                      <Link 
                        component={motion.a}
                        href="#"
                        underline="none"
                        whileHover={{ color: VERDE_MENTA }}
                        sx={{ 
                          color: theme.palette.grey[400],
                          transition: 'color 0.3s ease',
                          '&:hover': {
                            color: VERDE_MENTA
                          }
                        }}
                      >
                        {link}
                      </Link>
                    </ListItem>
                  ))}
                </List>
              </Grid>
            ))}
          </Grid>
          
          <Box 
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            sx={{ 
              mt: 8, 
              pt: 4, 
              borderTop: `1px solid ${withAlpha(theme.palette.grey[800], 0.5)}`, 
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'center', sm: 'center' },
              gap: 2
            }}
          >
            <Typography variant="body2" color={theme.palette.grey[500]}>
              © {new Date().getFullYear()} NeuroAgentes. Todos os direitos reservados.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Link 
                href="#" 
                underline="none" 
                sx={{ 
                  color: theme.palette.grey[500],
                  fontSize: '0.875rem',
                  transition: 'color 0.3s ease',
                  '&:hover': {
                    color: VERDE_MENTA
                  }
                }}
              >
                Termos
              </Link>
              <Link 
                href="#" 
                underline="none" 
                sx={{ 
                  color: theme.palette.grey[500],
                  fontSize: '0.875rem',
                  transition: 'color 0.3s ease',
                  '&:hover': {
                    color: VERDE_MENTA
                  }
                }}
              >
                Privacidade
              </Link>
              <Link 
                href="#" 
                underline="none" 
                sx={{ 
                  color: theme.palette.grey[500],
                  fontSize: '0.875rem',
                  transition: 'color 0.3s ease',
                  '&:hover': {
                    color: VERDE_MENTA
                  }
                }}
              >
                Cookies
              </Link>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;